/**
 * Inteligência operacional estruturada — 100 rotinas da Biblioteca Lean.
 * Enriquece objetivo, evidência, categoria, desvios e overrides de SWI/conformidade.
 */

export interface LeanLibraryIntelligenceEntry {
  objetivo: string;
  evidenciaEsperada: string;
  categoriaOperacional: string;
  itensDesvio: string[];
  swiPassos?: string[];
  itensConformidade?: string[];
  indicadoresPrimarios?: string[];
  indicadoresSecundarios?: string[];
}

function I(
  objetivo: string,
  evidencia: string,
  categoria: string,
  desvio: string[],
  extra?: Omit<
    LeanLibraryIntelligenceEntry,
    'objetivo' | 'evidenciaEsperada' | 'categoriaOperacional' | 'itensDesvio'
  >,
): LeanLibraryIntelligenceEntry {
  return {
    objetivo,
    evidenciaEsperada: evidencia,
    categoriaOperacional: categoria,
    itensDesvio: desvio,
    ...extra,
  };
}

export const LEAN_LIBRARY_INTELLIGENCE: Record<string, LeanLibraryIntelligenceEntry> = {
  // ── NÍVEL 1 — DIÁRIAS ──────────────────────────────────────
  'n1-d-01': I(
    'Assegurar continuidade operacional sem perda de contexto entre turnos.',
    'Ata de passagem assinada com pendências, prioridades e responsáveis definidos.',
    'Gestão Diária',
    [
      'Pendências críticas não transferidas',
      'Indicadores do turno não revisados',
      'Prioridades do turno não definidas',
      'Ata incompleta ou sem assinatura',
    ],
  ),

  'n1-d-02': I(
    'Garantir capacidade instalada (FTE) alinhada ao plano do turno.',
    'Quadro FTE atualizado com presença real, alocação e gaps identificados.',
    'Pessoas',
    [
      'Ausências não cobertas por backup',
      'Alocação divergente do plano de turno',
      'Postos críticos descobertos',
      'FTE não atualizado no início do turno',
    ],
  ),

  'n1-d-03': I(
    'Garantir cobertura de competências.',
    'Matriz conferida com cobertura validada e gaps de polivalência tratados.',
    'Pessoas',
    ['Postos sem backup'],
    {
      swiPassos: ['Lacunas de treinamento', 'Cobertura por posto', 'Backups definidos'],
      itensConformidade: ['Cobertura adequada'],
      indicadoresPrimarios: ['Polivalência', 'Cobertura de Competências', 'Horas de Treinamento'],
    },
  ),

  'n1-d-04': I(
    'Sincronizar execução do turno e remover impedimentos imediatos.',
    'Ata de reunião de camada com TOP 3 prioridades, donos e prazos registrados.',
    'Gestão Diária',
    [
      'Impedimentos sem dono ou prazo',
      'Ações da reunião anterior não revisadas',
      'Indicadores críticos não apresentados',
      'Escalonamentos necessários não realizados',
    ],
  ),

  'n1-d-05': I(
    'Detectar desvios de ritmo antes do fechamento do turno.',
    'Quadro hora a hora atualizado com plano vs real, causas e ações de recuperação.',
    'Performance',
    [
      'Horas com desvio sem causa registrada',
      'Quadro desatualizado por mais de 1 hora',
      'Meta do turno invisível para a equipe',
      'Desvio persistente sem escalonamento',
    ],
  ),

  'n1-d-06': I(
    'Garantir recuperação e sustentação da eficiência.',
    'Registro OEE do turno com análise de A/P/Q e plano de recuperação ativo.',
    'Performance',
    ['Meta abaixo sem ação'],
    {
      swiPassos: ['Disponibilidade', 'Performance', 'Qualidade', 'Plano de recuperação'],
      itensConformidade: ['Meta atingida ou plano ativo'],
      indicadoresPrimarios: ['OEE', 'Disponibilidade', 'Performance', 'Qualidade'],
    },
  ),

  'n1-d-07': I(
    'Identificar perdas e obstáculos operacionais.',
    'Checklist gemba com perdas mapeadas, observações e ações com responsável.',
    'Gemba',
    ['Perda observada', 'Restrição sem ação'],
    {
      swiPassos: [
        'Fluxo normal',
        'Esperas',
        'Retrabalho',
        'Falta de material',
        'Falta de operador',
        'Restrições',
      ],
      itensConformidade: ['Fluxo sem interrupções relevantes'],
      indicadoresPrimarios: ['OEE', 'Produtividade', 'Lead Time', 'Eficiência'],
    },
  ),

  'n1-d-08': I(
    'Garantir execução conforme trabalho padrão aprovado nas estações.',
    'Ficha de auditoria de trabalho padrão com conformidades, NCs e ações abertas.',
    'Qualidade',
    [
      'Sequência de operação divergente do padrão',
      'Tempos de ciclo fora da faixa aceitável',
      'Trabalho padrão ausente ou desatualizado no posto',
      'NC identificada sem plano de correção',
    ],
  ),

  'n1-d-09': I(
    'Eliminar condições e comportamentos inseguros.',
    'Checklist de segurança preenchido com tratativas documentadas e prazos definidos.',
    'Segurança',
    ['Risco crítico identificado', 'Falta de EPI', 'Tratativas vencidas'],
    {
      swiPassos: [
        'Uso correto de EPI',
        'Proteções instaladas',
        'Bloqueios respeitados',
        'Área organizada',
        'Tratativas anteriores concluídas',
      ],
      itensConformidade: ['Nenhum risco crítico', 'EPI correto', 'Pendências tratadas'],
      indicadoresPrimarios: ['Acidentes', 'Near Miss', 'Taxa de Incidentes', 'Auditoria de Segurança'],
    },
  ),

  'n1-d-10': I(
    'Sustentar disciplina visual e organização no posto de trabalho.',
    'Score 5S por área registrado com evidências fotográficas quando aplicável.',
    'Qualidade',
    [
      'Itens desnecessários no posto (Seiri)',
      'Falta de lugar definido (Seiton)',
      'Sujeira ou contaminação visível (Seiso)',
      'Padrão 5S não sustentado (Shitsuke)',
    ],
  ),

  'n1-d-11': I(
    'Impedir escape de defeitos para etapas subsequentes do processo.',
    'Registro de auditoria de qualidade com NCs, contenções e pontos de controle verificados.',
    'Qualidade',
    [
      'Ponto de controle não executado',
      'Defeito detectado sem contenção imediata',
      'Padrão de qualidade divergente do especificado',
      'Registro de inspeção ausente ou incompleto',
    ],
  ),

  'n1-d-12': I(
    'Impedir que anomalias evoluam sem contenção e tratamento estruturado.',
    'Registro de anomalia com contenção aplicada, dono definido e prazo de resolução.',
    'Kaizen',
    [
      'Anomalia sem contenção imediata',
      'Registro Andon não realizado',
      'Anomalia sem dono ou prazo',
      'Recorrência da mesma anomalia sem análise',
    ],
  ),

  'n1-d-13': I(
    'Desenvolver competência operacional via método TWI (JI/JM).',
    'Registro TWI com operador treinado, método demonstrado e verificação de aprendizado.',
    'Pessoas',
    [
      'Treinamento sem demonstração prática (JI)',
      'Operador não verificado após instrução',
      'Método treinado diverge do trabalho padrão',
      'Registro TWI incompleto ou ausente',
    ],
  ),

  'n1-d-14': I(
    'Garantir execução do padrão SMED.',
    'Tempo e sequência de setup registrados comparados ao padrão SMED documentado.',
    'SMED',
    ['Desvios do roteiro', 'Tempo acima da meta'],
    {
      swiPassos: [
        'Sequência seguida',
        'Ferramentas preparadas',
        'Checklists concluídos',
        'Ajustes controlados',
      ],
      itensConformidade: ['Setup dentro do padrão'],
      indicadoresPrimarios: ['Tempo de Setup', '% Setup na Meta', 'Disponibilidade'],
    },
  ),

  'n1-d-15': I(
    'Antecipar e eliminar bloqueios de produção por restrições operacionais.',
    'Lista de restrições críticas com status de resolução e responsável atualizado.',
    'Gestão Diária',
    [
      'Material crítico em falta sem plano',
      'Ferramenta ou fixture indisponível',
      'Informação de produção desatualizada',
      'Restrição conhecida sem ação de mitigação',
    ],
  ),

  // ── NÍVEL 1 — SEMANAIS ─────────────────────────────────────
  'n1-s-01': I(
    'Planejar desenvolvimento e cobertura de competências da equipe.',
    'Matriz de competências atualizada com gaps mapeados e plano de treinamento definido.',
    'Pessoas',
    [
      'Gaps de competência não mapeados',
      'Plano de treinamento ausente para lacunas críticas',
      'Matriz desatualizada há mais de uma semana',
      'Certificações vencidas sem reciclagem planejada',
    ],
  ),

  'n1-s-02': I(
    'Garantir fechamento de pendências críticas via ciclo PDCA.',
    'Painel de planos de ação com status, % de conclusão e ações atrasadas revisadas.',
    'Kaizen',
    [
      'Ações críticas vencidas sem justificativa',
      'Plano sem evidência de execução',
      'Ações abertas sem dono definido',
      'Pendências recorrentes sem análise de causa',
    ],
  ),

  'n1-s-03': I(
    'Eliminar causas raiz de problemas recorrentes na área.',
    'Ata de solução de problemas (5W2H ou A3) com causa raiz, dono e prazo definidos.',
    'Kaizen',
    [
      'Análise superficial sem causa raiz',
      'Problema recorrente sem contramedida',
      'Sessão sem participação dos operadores afetados',
      'Ação corretiva sem prazo ou responsável',
    ],
  ),

  'n1-s-04': I(
    'Garantir condição básica dos equipamentos.',
    'Checklist TPM com condição básica validada e anomalias encaminhadas à manutenção.',
    'TPM',
    ['Falha de condição básica'],
    {
      swiPassos: ['Limpeza', 'Lubrificação', 'Inspeção', 'Etiquetas', 'Anomalias'],
      itensConformidade: ['Equipamento dentro do padrão'],
      indicadoresPrimarios: ['MTBF', 'MTTR', 'Quebras', 'Disponibilidade'],
    },
  ),

  'n1-s-05': I(
    'Ajustar foco da equipe conforme tendência de desempenho semanal.',
    'Quadro de KPIs da área com comentários de tendência e ações direcionadas.',
    'Performance',
    [
      'KPI abaixo da meta sem plano de recuperação',
      'Tendência negativa não analisada',
      'Indicadores secundários ignorados no diagnóstico',
      'Comentários de tendência ausentes no painel',
    ],
  ),

  'n1-s-06': I(
    'Engajar equipe em kaizen sistemático com priorização de ideias.',
    'Lista de melhorias priorizadas com dono, impacto estimado e status de implementação.',
    'Kaizen',
    [
      'Ideias coletadas sem priorização',
      'Melhorias aprovadas sem implementação',
      'Equipe não participa do fórum de melhoria',
      'Ganhos de kaizen não quantificados',
    ],
  ),

  'n1-s-07': I(
    'Evitar retrocesso (yo-yo) após implementação de melhorias.',
    'Checklist de sustentação com status de cada melhoria e evidências de aderência.',
    'Kaizen',
    [
      'Melhoria revertida ao estado anterior',
      'Padrão pós-kaizen não documentado',
      'Auditoria de sustentação não realizada',
      'Treinamento de sustentação pendente',
    ],
  ),

  'n1-s-08': I(
    'Escalonar obstáculos que exigem suporte da camada superior (supervisor).',
    'Ata de reunião de camada com escalonamentos, decisões e prazos acordados.',
    'Gestão Diária',
    [
      'Obstáculo crítico não escalonado',
      'Escalonamento sem retorno da camada 2',
      'Ata de camada incompleta',
      'Problema estrutural tratado apenas no turno',
    ],
  ),

  // ── NÍVEL 2 — DIÁRIAS ──────────────────────────────────────
  'n2-d-01': I(
    'Garantir fluxo de informação e sincronização entre camadas de liderança.',
    'Ata de reunião escalonada camada 2 com TOP 3 riscos e ações por líder.',
    'Gestão Diária',
    [
      'Riscos críticos não comunicados à camada 2',
      'Líderes ausentes na reunião de camada',
      'Informações de turno não consolidadas',
      'Decisões da camada 2 não repassadas',
    ],
  ),

  'n2-d-02': I(
    'Validar aderência ao padrão operacional em escala multi-linha.',
    'Relatório gemba estruturado com observações, desvios e ações atribuídas por líder.',
    'Gemba',
    [
      'Desvio sistêmico não registrado',
      'Gemba sem roteiro ou foco definido',
      'Ações abertas sem acompanhamento do supervisor',
      'Padrão divergente entre linhas da mesma área',
    ],
  ),

  'n2-d-03': I(
    'Antecipar desvios de meta diária via monitoramento de KPIs operacionais.',
    'Dashboard de indicadores revisado com comentários e alertas para desvios críticos.',
    'Performance',
    [
      'KPI crítico abaixo da meta sem intervenção',
      'Dashboard desatualizado no turno',
      'Comentários de análise ausentes',
      'Desvio não comunicado aos líderes afetados',
    ],
  ),

  'n2-d-04': I(
    'Remover barreiras fora do alcance da camada 1 de liderança.',
    'Registro de escalonamento com problema crítico, ação definida e prazo acordado.',
    'Gestão Diária',
    [
      'Problema crítico sem escalonamento formal',
      'Barreira sem dono na camada 2',
      'Prazo de resolução não definido',
      'Problema recorrente escalonado sem análise de causa',
    ],
  ),

  'n2-d-05': I(
    'Garantir disciplina de liderança via execução do TPL pelos líderes.',
    'Score de aderência TPL por líder com rotinas auditadas e desvios registrados.',
    'Pessoas',
    [
      'Rotina TPL não executada pelo líder',
      'Auditoria de TPL não realizada no turno',
      'Desvio de rotina sem plano de correção',
      'Líder sem registro de gemba ou passagem de turno',
    ],
  ),

  'n2-d-06': I(
    'Proteger capacidade produtiva monitorando recursos críticos.',
    'Status documentado de pessoas, máquinas e materiais críticos com plano de contingência.',
    'Gestão Diária',
    [
      'Recurso crítico indisponível sem plano B',
      'Máquina gargalo com restrição não tratada',
      'Material estratégico em risco de ruptura',
      'Ausência de operador-chave sem cobertura',
    ],
  ),

  'n2-d-07': I(
    'Acelerar fluxo operacional removendo impedimentos reportados pelos líderes.',
    'Lista de barreiras com status de remoção, dono e evidência de desbloqueio.',
    'Kaizen',
    [
      'Barreira reportada sem ação do supervisor',
      'Impedimento cross-funcional não escalonado',
      'Remoção de barreira sem validação no gemba',
      'Mesma barreira recorrente sem contramedida estrutural',
    ],
  ),

  // ── NÍVEL 2 — SEMANAIS ─────────────────────────────────────
  'n2-s-01': I(
    'Sincronizar plano de produção (PCP) com capacidade real da área.',
    'Ata de alinhamento com PCP contendo ajustes de plano acordados e restrições mapeadas.',
    'Gestão Diária',
    [
      'Plano de produção incompatível com capacidade',
      'Ajuste de sequenciamento não acordado com PCP',
      'Restrição de capacidade não comunicada',
      'Mudança de plano sem atualização do hora a hora',
    ],
  ),

  'n2-s-02': I(
    'Priorizar ataque às maiores perdas operacionais via análise quantitativa.',
    'Pareto de perdas (tempo, qualidade, disponibilidade) com ações priorizadas por impacto.',
    'Performance',
    [
      'Perdas não quantificadas',
      'Pareto sem ações vinculadas às maiores perdas',
      'Análise limitada a um pilar (só qualidade ou só disponibilidade)',
      'Perda crônica sem projeto de redução',
    ],
  ),

  'n2-s-03': I(
    'Reduzir recorrência de defeitos via gestão rigorosa de não conformidades.',
    'Painel de NCs com status, tendência, eficácia de ações e NCs críticas abertas.',
    'Qualidade',
    [
      'NC crítica aberta sem contenção',
      'Ação corretiva ineficaz (recorrência)',
      'NC sem análise de causa raiz',
      'Prazo de fechamento de NC vencido',
    ],
  ),

  'n2-s-04': I(
    'Proteger disponibilidade crítica analisando paradas e falhas de equipamento.',
    'Registro de falhas críticas com impacto em OEE, causa e plano corretivo definido.',
    'TPM',
    [
      'Falha crítica sem análise de causa',
      'Parada recorrente sem contramedida',
      'Plano corretivo vencido sem execução',
      'Impacto em OEE não quantificado',
    ],
  ),

  'n2-s-05': I(
    'Desenvolver liderança via coaching individual estruturado com cada líder.',
    'Notas de one-on-one com compromissos, feedback e plano de desenvolvimento por líder.',
    'Pessoas',
    [
      'One-on-one não realizado na semana',
      'Compromissos anteriores não revisados',
      'Feedback sem plano de desenvolvimento',
      'Líder com desempenho crítico sem coaching intensivo',
    ],
  ),

  'n2-s-06': I(
    'Mitigar risco de gargalo de competência e ausência não coberta.',
    'Matriz de polivalência revisada com cobertura por posto e plano para gaps críticos.',
    'Pessoas',
    [
      'Posto crítico com cobertura inferior a 2 operadores',
      'Gap de competência em estação gargalo',
      'Matriz de polivalência desatualizada',
      'Treinamento de backup não planejado',
    ],
  ),

  'n2-s-07': I(
    'Evitar regressão após eventos kaizen via auditoria de sustentação.',
    'Checklist pós-kaizen por área com evidências de aderência ao novo padrão.',
    'Kaizen',
    [
      'Padrão pós-kaizen não sustentado',
      'Auditoria pós-kaizen não realizada',
      'Operadores não treinados no novo método',
      'Indicador de ganho do kaizen em deterioração',
    ],
  ),

  'n2-s-08': I(
    'Fechar ciclo PDCA dos planos de ação da supervisão.',
    'Painel de ações da supervisão atualizado com status, evidências e % de conclusão.',
    'Kaizen',
    [
      'Ação da supervisão vencida sem tratativa',
      'Plano sem evidência de eficácia',
      'Ações duplicadas ou sem priorização',
      'Compromisso de camada 2 não cumprido',
    ],
  ),

  'n2-s-09': I(
    'Direcionar foco da próxima semana com base em análise consolidada de KPIs.',
    'Relatório semanal de indicadores com tendências, desvios e prioridades definidas.',
    'Performance',
    [
      'KPI semanal abaixo da meta sem plano',
      'Tendência de três semanas negativa ignorada',
      'Relatório sem análise de causa',
      'Prioridades da semana não definidas',
    ],
  ),

  'n2-s-10': I(
    'Alinhar metas e prioridades da semana entre líderes e apoios.',
    'Ata de reunião de performance com metas, responsáveis e compromissos da semana.',
    'Performance',
    [
      'Metas da semana não acordadas',
      'Líderes-chave ausentes na reunião',
      'Compromissos sem dono ou prazo',
      'Desempenho da semana anterior não revisado',
    ],
  ),

  'n2-s-11': I(
    'Elevar obstáculos estruturais para a camada de coordenação.',
    'Ata de camada 3 solicitada com obstáculos, decisões pendentes e prazos.',
    'Gestão Diária',
    [
      'Obstáculo estrutural não escalonado ao coordenador',
      'Escalonamento sem documentação formal',
      'Decisão da camada 3 não repassada aos líderes',
      'Problema sistêmico tratado como pontual',
    ],
  ),

  // ── NÍVEL 2 — MENSAIS ──────────────────────────────────────
  'n2-m-01': I(
    'Avaliar cumprimento de metas mensais com fechamento formal de KPIs.',
    'Relatório mensal de KPIs assinado com análise de desvios e plano do próximo ciclo.',
    'Performance',
    [
      'KPI mensal sem fechamento formal',
      'Desvio de meta sem análise de causa',
      'Relatório sem assinatura do responsável',
      'Plano do próximo mês não definido',
    ],
  ),

  'n2-m-02': I(
    'Desenvolver pipeline de liderança via feedback estruturado de desempenho.',
    'Ficha de avaliação de desempenho por líder com metas, gaps e plano de desenvolvimento.',
    'Pessoas',
    [
      'Avaliação de desempenho não realizada',
      'Feedback sem exemplos comportamentais',
      'Plano de desenvolvimento ausente',
      'Líder com desempenho crítico sem PIP',
    ],
  ),

  'n2-m-03': I(
    'Planejar capacitação do próximo ciclo mapeando gaps de treinamento.',
    'Plano de treinamento mensal com necessidades mapeadas, cronograma e responsáveis.',
    'Pessoas',
    [
      'Necessidade de treinamento não mapeada',
      'Plano mensal sem cronograma',
      'Treinamento crítico não priorizado',
      'Horas de treinamento abaixo da meta',
    ],
  ),

  'n2-m-04': I(
    'Antecipar ajustes de turno ou investimentos via análise capacidade vs demanda.',
    'Estudo de capacidade documentado com cenários, gargalos e recomendações.',
    'Gestão Diária',
    [
      'Capacidade insuficiente para demanda prevista',
      'Gargalo de capacidade não identificado',
      'Estudo sem cenário de contingência',
      'Recomendação sem dono ou prazo',
    ],
  ),

  'n2-m-05': I(
    'Manter eficiência de mão de obra alinhando headcount ao orçamento e necessidade.',
    'Quadro de headcount com variações explicadas vs orçamento e necessidade operacional.',
    'Financeiro',
    [
      'Headcount acima do orçamento sem justificativa',
      'Vaga crítica descoberta sem reposição planejada',
      'Variação de headcount não documentada',
      'Estrutura desalinhada à demanda',
    ],
  ),

  // ── NÍVEL 3 — DIÁRIAS ──────────────────────────────────────
  'n3-d-01': I(
    'Validar padrão operacional e cultura lean em múltiplas células sob coordenação.',
    'Relatório gemba de coordenação com observações transversais e ações por área.',
    'Gemba',
    [
      'Desvio cultural entre células não endereçado',
      'Gemba sem cobertura de todas as áreas',
      'Padrão inconsistente entre supervisões',
      'Ações do gemba sem acompanhamento',
    ],
  ),

  'n3-d-02': I(
    'Antecipar risco de meta diária consolidada via KPIs críticos.',
    'Painel de indicadores críticos revisado com alertas e intervenções definidas.',
    'Performance',
    [
      'KPI crítico em zona vermelha sem intervenção',
      'Painel não revisado no dia',
      'Desvio não comunicado aos supervisores',
      'Meta diária consolidada em risco',
    ],
  ),

  'n3-d-03': I(
    'Desbloquear fluxo entre áreas atuando em barreiras escaladas pelos supervisores.',
    'Log de barreiras removidas com evidência de desbloqueio e tempo de resolução.',
    'Kaizen',
    [
      'Barreira cross-área sem ação do coordenador',
      'Desbloqueio sem validação no gemba',
      'Barreira recorrente sem solução estrutural',
      'Tempo de resolução acima do SLA',
    ],
  ),

  'n3-d-04': I(
    'Evitar normalização de falhas crônicas de longa duração.',
    'Status atualizado de problemas crônicos com tendência, dono e plano de erradicação.',
    'Kaizen',
    [
      'Problema crônico sem plano de erradicação',
      'Falha normalizada pela equipe',
      'Status de crônico desatualizado',
      'Problema crônico sem revisão de eficácia',
    ],
  ),

  'n3-d-05': I(
    'Acelerar resolução de causas sistêmicas em problemas multi-área.',
    'Ata de sessão de problema complexo com equipe multifuncional, causa e ações definidas.',
    'Kaizen',
    [
      'Problema complexo sem equipe multifuncional',
      'Análise sem ferramenta estruturada (A3/8D)',
      'Causa sistêmica não identificada',
      'Sessão sem decisões ou compromissos',
    ],
  ),

  // ── NÍVEL 3 — SEMANAIS ─────────────────────────────────────
  'n3-s-01': I(
    'Alinhar performance operacional entre áreas sob coordenação.',
    'Ata de reunião de performance operacional com metas, desvios e ações por supervisão.',
    'Performance',
    [
      'Área com desempenho crítico sem plano',
      'Metas não alinhadas entre supervisões',
      'Reunião sem análise de tendência',
      'Compromissos sem acompanhamento',
    ],
  ),

  'n3-s-02': I(
    'Direcionar ações corretivas com base em análise semanal de KPIs.',
    'Relatório KPI semanal da coordenação com tendências e prioridades de ação.',
    'Performance',
    [
      'KPI abaixo da meta sem ação corretiva',
      'Tendência negativa de três semanas',
      'Relatório sem priorização de ações',
      'Indicador crítico não monitorado',
    ],
  ),

  'n3-s-03': I(
    'Garantir avanço de iniciativas lean estruturantes em andamento.',
    'Painel de projetos lean com marcos, status e impedimentos por projeto.',
    'Kaizen',
    [
      'Projeto lean atrasado sem plano de recuperação',
      'Marco crítico não atingido',
      'Projeto sem sponsor ou dono',
      'Impedimento do projeto não escalonado',
    ],
  ),

  'n3-s-04': I(
    'Maximizar ganhos de melhoria via acompanhamento de projetos kaizen.',
    'Status kaizen por área com ganhos quantificados e sustentação verificada.',
    'Kaizen',
    [
      'Ganho do kaizen não quantificado',
      'Projeto kaizen parado sem justificativa',
      'Sustentação do kaizen não verificada',
      'Evento kaizen sem follow-up pós-implementação',
    ],
  ),

  'n3-s-05': I(
    'Manter rigor na solução de problemas via gestão de A3 abertos.',
    'Painel A3 atualizado com status, etapas do PDCA e prazos por problema.',
    'Kaizen',
    [
      'A3 aberto sem avanço no PDCA',
      'Análise de causa superficial no A3',
      'A3 vencido sem revisão',
      'Contramedida sem verificação de eficácia',
    ],
  ),

  'n3-s-06': I(
    'Fechar loop de qualidade com gestão rigorosa de relatórios 8D.',
    'Painel 8D com status por etapa, prazos e eficácia das ações corretivas.',
    'Qualidade',
    [
      '8D parado em etapa intermediária',
      'Ação corretiva sem verificação de eficácia',
      'Prazo de resposta ao cliente vencido',
      '8D sem equipe multifuncional formada',
    ],
  ),

  'n3-s-07': I(
    'Elevar maturidade da camada 2 via coaching estruturado de supervisores.',
    'Notas de coaching com compromissos, feedback e plano de desenvolvimento por supervisor.',
    'Pessoas',
    [
      'Coaching não realizado na semana',
      'Supervisor crítico sem plano de desenvolvimento',
      'Compromissos de coaching não revisados',
      'Feedback sem exemplos situacionais',
    ],
  ),

  'n3-s-08': I(
    'Garantir execução de compromissos via gestão de planos de ação da coordenação.',
    'Painel de ações da coordenação com status, evidências e ações vencidas tratadas.',
    'Kaizen',
    [
      'Ação da coordenação vencida',
      'Compromisso sem evidência de execução',
      'Plano sem priorização por impacto',
      'Ação recorrente sem análise de causa',
    ],
  ),

  'n3-s-09': I(
    'Aumentar throughput sistêmico atacando gargalos do fluxo produtivo.',
    'Mapa de gargalos com ações de aumento de capacidade e redução de restrições.',
    'Performance',
    [
      'Gargalo identificado sem ação',
      'Capacidade do gargalo não monitorada',
      'Ação no gargalo sem validação de throughput',
      'Gargalo migrado sem solução na origem',
    ],
  ),

  'n3-s-10': I(
    'Ajustar alocação entre áreas com base em capacidade semanal vs plano.',
    'Estudo de capacidade semanal com realocação proposta e restrições mapeadas.',
    'Gestão Diária',
    [
      'Capacidade semanal insuficiente para o plano',
      'Realocação entre áreas não acordada',
      'Restrição de capacidade não comunicada ao PCP',
      'Gargalo semanal não endereçado',
    ],
  ),

  'n3-s-11': I(
    'Elevar decisões estratégico-táticas para a camada gerencial.',
    'Ata de camada 4 com obstáculos, decisões solicitadas e prazos acordados.',
    'Gestão Diária',
    [
      'Decisão estratégica pendente sem escalonamento',
      'Ata de camada 4 incompleta',
      'Obstáculo sistêmico não elevado à gerência',
      'Retorno da camada 4 não comunicado',
    ],
  ),

  // ── NÍVEL 3 — MENSAIS ──────────────────────────────────────
  'n3-m-01': I(
    'Alinhar custo de pessoal à demanda via gestão consolidada de headcount.',
    'Relatório de headcount da coordenação com variações, justificativas e projeção.',
    'Financeiro',
    [
      'Headcount acima do orçamento aprovado',
      'Variação não justificada documentalmente',
      'Projeção de headcount desalinhada à demanda',
      'Vaga crítica sem processo de reposição',
    ],
  ),

  'n3-m-02': I(
    'Identificar oportunidades de redução via análise de custos operacionais.',
    'Relatório de custos variáveis e fixos com oportunidades de redução priorizadas.',
    'Financeiro',
    [
      'Custo variável acima do orçamento sem análise',
      'Oportunidade de redução identificada sem ação',
      'Desvio de custo sem plano de contenção',
      'Relatório sem comparativo mês anterior',
    ],
  ),

  'n3-m-03': I(
    'Garantir maturidade do sistema de gestão integrado (SGQ/SGA).',
    'Relatório de auditoria sistêmica com achados, classificação e planos de ação.',
    'Qualidade',
    [
      'Não conformidade sistêmica sem tratativa',
      'Auditoria com achado crítico aberto',
      'Procedimento obrigatório não seguido',
      'Plano de ação da auditoria vencido',
    ],
  ),

  'n3-m-04': I(
    'Conectar operação diária aos direcionadores estratégicos da área.',
    'Ata de revisão estratégica com alinhamento de prioridades e desdobramento tático.',
    'Hoshin',
    [
      'Operação desconectada da estratégia',
      'Prioridade estratégica sem desdobramento tático',
      'Revisão estratégica não realizada no mês',
      'Indicador estratégico sem acompanhamento',
    ],
  ),

  'n3-m-05': I(
    'Identificar ganhos e perdas de eficiência via análise mensal de produtividade.',
    'Relatório de produtividade mensal com tendência, causas e plano de melhoria.',
    'Performance',
    [
      'Produtividade abaixo da meta sem plano',
      'Perda de eficiência não quantificada',
      'Tendência negativa de três meses',
      'Ganho de produtividade não sustentado',
    ],
  ),

  'n3-m-06': I(
    'Priorizar CAPEX/OPEX operacional via gestão do pipeline de investimentos.',
    'Lista de investimentos priorizados com ROI estimado, status e aprovações.',
    'Financeiro',
    [
      'Investimento crítico sem priorização',
      'CAPEX aprovado sem cronograma de execução',
      'ROI não calculado para investimento proposto',
      'Pipeline de investimentos desatualizado',
    ],
  ),

  // ── NÍVEL 4 — DIÁRIAS ──────────────────────────────────────
  'n4-d-01': I(
    'Manter presença gerencial no gemba em plantas e áreas críticas.',
    'Relatório gemba gerencial com observações, decisões e compromissos de follow-up.',
    'Gemba',
    [
      'Gemba gerencial não realizado',
      'Área crítica sem visita no período',
      'Observação sem ação de follow-up',
      'Desvio sistêmico não endereçado pela gerência',
    ],
  ),

  'n4-d-02': I(
    'Antecipar risco de meta corporativa via monitoramento de KPIs críticos da unidade.',
    'Painel crítico gerencial revisado com alertas e intervenções definidas.',
    'Performance',
    [
      'KPI corporativo em risco sem intervenção',
      'Painel crítico desatualizado',
      'Desvio não comunicado à diretoria',
      'Meta da unidade comprometida',
    ],
  ),

  'n4-d-03': I(
    'Acelerar decisões cross-funcionais desbloqueando barreiras estratégicas.',
    'Log de barreiras estratégicas com status, decisões tomadas e prazos.',
    'Hoshin',
    [
      'Barreira estratégica sem decisão gerencial',
      'Desbloqueio cross-funcional pendente',
      'Barreira recorrente sem solução estrutural',
      'Decisão tomada sem comunicação às áreas',
    ],
  ),

  // ── NÍVEL 4 — SEMANAIS ─────────────────────────────────────
  'n4-s-01': I(
    'Alinhar prioridades táticas entre coordenadores da unidade.',
    'Ata de camada gerencial com prioridades, riscos e compromissos por coordenação.',
    'Gestão Diária',
    [
      'Prioridade tática não alinhada entre áreas',
      'Coordenador crítico ausente na reunião',
      'Compromisso gerencial sem dono',
      'Risco sistêmico não discutido',
    ],
  ),

  'n4-s-02': I(
    'Monitorar progresso de metas estratégicas via KPIs da unidade.',
    'Relatório KPI estratégico com tendências, desvios e plano de recuperação.',
    'Hoshin',
    [
      'KPI estratégico abaixo da meta sem plano',
      'Desvio estratégico não escalonado',
      'Tendência negativa não analisada',
      'Meta anual em risco',
    ],
  ),

  'n4-s-03': I(
    'Garantir entrega de iniciativas-chave via gestão de projetos estratégicos.',
    'Painel de projetos estratégicos com marcos, status e impedimentos críticos.',
    'Hoshin',
    [
      'Projeto estratégico atrasado sem plano',
      'Marco crítico não atingido',
      'Projeto sem sponsor executivo',
      'Impedimento não escalonado à diretoria',
    ],
  ),

  'n4-s-04': I(
    'Elevar bench de liderança via coaching de coordenadores e supervisores-chave.',
    'Notas de coaching gerencial com compromissos e plano de desenvolvimento por líder.',
    'Pessoas',
    [
      'Líder-chave sem sessão de coaching',
      'Plano de desenvolvimento de liderança ausente',
      'Compromisso de coaching não cumprido',
      'Líder crítico sem plano de sucessão',
    ],
  ),

  'n4-s-05': I(
    'Impedir perpetuação de falhas sistêmicas via gestão de problemas crônicos.',
    'Painel de problemas crônicos com status, tendência e plano de erradicação.',
    'Kaizen',
    [
      'Problema crônico sem plano de erradicação',
      'Falha sistêmica normalizada',
      'Crônico sem revisão de eficácia',
      'Problema crônico impactando KPI estratégico',
    ],
  ),

  'n4-s-06': I(
    'Direcionar foco da liderança com base em performance consolidada da planta.',
    'Ata de revisão de performance com prioridades, metas e ações por área.',
    'Performance',
    [
      'Performance da planta abaixo da meta',
      'Área crítica sem plano de recuperação',
      'Prioridades da semana não definidas',
      'Tendência negativa não endereçada',
    ],
  ),

  'n4-s-07': I(
    'Fechar compromissos estratégicos via gestão de planos de ação gerenciais.',
    'Painel de ações estratégicas com status, evidências e ações vencidas tratadas.',
    'Hoshin',
    [
      'Ação estratégica vencida sem tratativa',
      'Compromisso gerencial sem evidência',
      'Plano sem priorização por impacto estratégico',
      'Ação recorrente sem revisão de eficácia',
    ],
  ),

  'n4-s-08': I(
    'Alinhar riscos e decisões executivas escalando para a diretoria.',
    'Ata de camada 5 com riscos, decisões solicitadas e prazos acordados.',
    'Gestão Diária',
    [
      'Risco executivo não escalonado à diretoria',
      'Decisão pendente sem documentação',
      'Ata de camada 5 incompleta',
      'Retorno da diretoria não comunicado',
    ],
  ),

  // ── NÍVEL 4 — MENSAIS ──────────────────────────────────────
  'n4-m-01': I(
    'Conectar operação ao P&L via análise do resultado financeiro da unidade.',
    'Relatório financeiro mensal com desvios orçamentários, causas e ações corretivas.',
    'Financeiro',
    [
      'Desvio orçamentário sem análise de causa',
      'Custo operacional acima do planejado',
      'Relatório financeiro sem assinatura',
      'Ação de contenção de custo não definida',
    ],
  ),

  'n4-m-02': I(
    'Identificar gaps de produtividade vs benchmark interno ou externo.',
    'Relatório de produtividade consolidada com comparativo, tendência e plano de melhoria.',
    'Performance',
    [
      'Produtividade abaixo do benchmark',
      'Gap de eficiência não quantificado',
      'Tendência negativa de produtividade',
      'Plano de melhoria de produtividade ausente',
    ],
  ),

  'n4-m-03': I(
    'Otimizar estrutura organizacional alinhando headcount ao orçamento da unidade.',
    'Relatório de headcount da unidade com variações vs orçamento e recomendações.',
    'Financeiro',
    [
      'Headcount acima do orçamento aprovado',
      'Estrutura desalinhada à demanda',
      'Variação não justificada',
      'Reposição crítica sem aprovação',
    ],
  ),

  'n4-m-04': I(
    'Planejar roadmap de transformação lean via avaliação de maturidade.',
    'Score de maturidade lean com dimensões avaliadas, gaps e plano de evolução.',
    'Kaizen',
    [
      'Dimensão lean crítica com score baixo',
      'Avaliação de maturidade não realizada',
      'Gap de maturidade sem plano de evolução',
      'Retrocesso em dimensão avaliada anteriormente',
    ],
  ),

  'n4-m-05': I(
    'Priorizar iniciativas de médio prazo via gestão do roadmap de melhorias.',
    'Roadmap de melhorias atualizado com prioridades, marcos e responsáveis.',
    'Kaizen',
    [
      'Iniciativa prioritária atrasada no roadmap',
      'Roadmap desatualizado',
      'Melhoria sem owner definido',
      'Marco de médio prazo não atingido',
    ],
  ),

  'n4-m-06': I(
    'Garantir continuidade de liderança via gestão de pipeline de talentos.',
    'Relatório de talentos e sucessão com posições-chave, readiness e planos.',
    'Pessoas',
    [
      'Posição crítica sem sucessor identificado',
      'Pipeline de talentos desatualizado',
      'Plano de sucessão ausente para gerência',
      'Talento-chave com risco de saída sem retenção',
    ],
  ),

  'n4-m-07': I(
    'Planejar expansão ou retração com base em capacidade vs demanda de médio prazo.',
    'Estudo de capacidade operacional com cenários, investimentos e recomendações.',
    'Gestão Diária',
    [
      'Capacidade insuficiente para demanda de médio prazo',
      'Investimento de capacidade não priorizado',
      'Cenário de retração sem plano de contingência',
      'Estudo sem recomendação formal',
    ],
  ),

  // ── NÍVEL 5 — SEMANAIS ─────────────────────────────────────
  'n5-s-01': I(
    'Demonstrar compromisso executivo com a operação via presença no gemba.',
    'Relatório gemba executivo com observações, decisões e comunicação à organização.',
    'Gemba',
    [
      'Gemba executivo não realizado na semana',
      'Planta crítica sem presença executiva',
      'Observação sem comunicação à organização',
      'Decisão do gemba sem follow-up',
    ],
  ),

  'n5-s-02': I(
    'Alinhar diretoria à execução via fórum de performance corporativa.',
    'Ata de reunião de performance corporativa com metas, desvios e direcionamentos.',
    'Performance',
    [
      'Performance corporativa abaixo da meta',
      'Unidade crítica sem plano de recuperação',
      'Direcionamento executivo não comunicado',
      'Meta corporativa em risco',
    ],
  ),

  'n5-s-03': I(
    'Monitorar metas do Hoshin via KPIs estratégicos corporativos.',
    'Relatório de indicadores estratégicos com progresso vs Hoshin e desvios críticos.',
    'Hoshin',
    [
      'Indicador Hoshin abaixo da meta sem plano',
      'Desdobramento estratégico desalinhado',
      'Meta anual corporativa em risco',
      'KPI estratégico sem acompanhamento semanal',
    ],
  ),

  'n5-s-04': I(
    'Garantir foco em iniciativas críticas via gestão de projetos prioritários.',
    'Painel de projetos prioritários com marcos, status e impedimentos executivos.',
    'Hoshin',
    [
      'Projeto prioritário atrasado sem intervenção',
      'Iniciativa crítica sem sponsor da diretoria',
      'Marco executivo não atingido',
      'Portfólio sem priorização clara',
    ],
  ),

  'n5-s-05': I(
    'Antecipar crises operacionais via gestão de riscos e continuidade.',
    'Relatório de riscos operacionais com classificação, mitigação e planos de contingência.',
    'Segurança',
    [
      'Risco operacional crítico sem mitigação',
      'Plano de contingência ausente ou desatualizado',
      'Risco recorrente sem tratativa estrutural',
      'Near miss corporativo sem investigação',
    ],
  ),

  'n5-s-06': I(
    'Alinhar board e operação via escalonamento executivo à presidência.',
    'Ata de camada executiva com riscos, decisões e compromissos com a presidência.',
    'Gestão Diária',
    [
      'Risco corporativo não escalonado à presidência',
      'Decisão executiva pendente sem documentação',
      'Ata de camada executiva incompleta',
      'Compromisso com board não cumprido',
    ],
  ),

  // ── NÍVEL 5 — MENSAIS ──────────────────────────────────────
  'n5-m-01': I(
    'Garantir desdobramento estratégico via revisão do planejamento Hoshin Kanri.',
    'Relatório Hoshin Kanri com objetivos anuais, desdobramento por nível e catchball documentado.',
    'Hoshin',
    [
      'Objetivo Hoshin sem desdobramento por nível',
      'Catchball não realizado entre camadas',
      'Meta anual sem indicador de progresso',
      'Revisão Hoshin não realizada no mês',
    ],
  ),

  'n5-m-02': I(
    'Avaliar progresso anual das metas estratégicas corporativas.',
    'Relatório de metas estratégicas com progresso YTD, desvios e plano de recuperação.',
    'Hoshin',
    [
      'Meta estratégica abaixo do progresso esperado',
      'Desvio anual sem plano de recuperação',
      'Meta sem indicador mensurável',
      'Progresso YTD não reportado',
    ],
  ),

  'n5-m-03': I(
    'Garantir continuidade de liderança sênior via gestão de sucessão executiva.',
    'Relatório de sucessão e talentos com posições C-level, readiness e planos de desenvolvimento.',
    'Pessoas',
    [
      'Posição executiva sem sucessor identificado',
      'Pipeline de liderança sênior desatualizado',
      'Plano de sucessão não testado',
      'Talento executivo-chave com risco de saída',
    ],
  ),

  'n5-m-04': I(
    'Priorizar alocação de capital via gestão do portfólio de investimentos corporativos.',
    'Relatório de investimentos com priorização, ROI, status e aprovações do board.',
    'Financeiro',
    [
      'Investimento corporativo sem ROI calculado',
      'CAPEX crítico atrasado sem justificativa',
      'Portfólio sem priorização pelo board',
      'Investimento aprovado sem cronograma',
    ],
  ),

  'n5-m-05': I(
    'Medir maturidade sistêmica lean via auditoria executiva corporativa.',
    'Relatório de auditoria lean executiva com score por dimensão, gaps e roadmap de evolução.',
    'Kaizen',
    [
      'Dimensão lean corporativa com score crítico',
      'Auditoria executiva não realizada',
      'Gap sistêmico sem plano de transformação',
      'Retrocesso em maturidade lean corporativa',
    ],
  ),

  'n5-m-06': I(
    'Conectar estratégia ao resultado via análise financeira consolidada.',
    'Relatório de resultados financeiros consolidados com P&L, desvios e ações corretivas.',
    'Financeiro',
    [
      'Resultado financeiro abaixo do orçamento',
      'Desvio de margem sem análise de causa',
      'Ação de recuperação financeira não definida',
      'Relatório consolidado sem assinatura executiva',
    ],
  ),

  'n5-m-07': I(
    'Balancear pipeline de transformação via gestão do portfólio de projetos corporativos.',
    'Relatório de portfólio de projetos com status, priorização e balanceamento de recursos.',
    'Hoshin',
    [
      'Portfólio sobrecarregado sem priorização',
      'Projeto corporativo crítico atrasado',
      'Recursos insuficientes para pipeline',
      'Projeto sem alinhamento à estratégia',
    ],
  ),

  'n5-m-08': I(
    'Elevar governança e compliance via avaliação da maturidade do sistema de gestão.',
    'Relatório de maturidade do sistema corporativo com dimensões, gaps e plano de evolução.',
    'Qualidade',
    [
      'Dimensão de governança com maturidade baixa',
      'Não conformidade sistêmica corporativa aberta',
      'Auditoria de sistema não realizada',
      'Gap de compliance sem plano de adequação',
    ],
  ),
};

export const LEAN_LIBRARY_INTELLIGENCE_IDS = Object.keys(LEAN_LIBRARY_INTELLIGENCE);
